import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { KeyboardEvent, MutableRefObject } from 'react';

import { isRTL } from '@skyscanner-internal/backpack-web/bpk-react-utils';

export function useScrollToInitialImage(
  initialImageIndex: number,
  imagesRef: MutableRefObject<Array<HTMLElement | null>>,
) {
  useEffect(() => {
    const element = imagesRef.current[initialImageIndex];
    if (element) {
      element.scrollIntoView({
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [initialImageIndex, imagesRef]);
}

export function useIntersectionObserver(
  onIntersecting: (index: number) => void,
  { root, threshold }: IntersectionObserverInit,
) {
  const callbackRef = useRef(onIntersecting);

  useEffect(() => {
    callbackRef.current = onIntersecting;
  });

  const observe = useMemo<(element: HTMLElement | null) => void>(() => {
    if (!root) return () => {};
    const observer = new IntersectionObserver(
      (entries) => {
        const shownEntry = entries.find((entry) => entry.isIntersecting);
        if (!shownEntry) {
          return;
        }
        const { index } = (shownEntry.target as HTMLElement).dataset;
        if (index) {
          callbackRef.current(parseInt(index, 10));
        }
      },
      { root, threshold },
    );

    const observeElement = (element: HTMLElement | null) => {
      if (element && observer) {
        observer.observe(element);
      }
    };

    return observeElement;
  }, [root, threshold]);

  return observe;
}

type KeyboardHandlers<T extends HTMLElement> = {
  [key in KeyboardEvent<T>['key']]: (event: KeyboardEvent<T>) => void;
};
function useKeyboardHandler<T extends HTMLElement>(
  handlers: KeyboardHandlers<T>,
) {
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  const onKeyDown = useCallback(
    (event: KeyboardEvent<T>) => handlersRef.current[event.key]?.(event),
    [],
  );

  return onKeyDown;
}

export function useKeyboardImageNavigation(
  toPrev: () => void,
  toNext: () => void,
) {
  return useKeyboardHandler(
    isRTL()
      ? {
          ArrowLeft: toNext,
          ArrowRight: toPrev,
        }
      : {
          ArrowLeft: toPrev,
          ArrowRight: toNext,
        },
  );
}
